/**
 * Tool Enhancement Configuration Panel
 *
 * Split-view layout:
 * - Left: List of all discoverable tools (from /admin/tools/available)
 * - Right: Enhancement form for selected tool
 *
 * Tool configs are matched by name (key). If a tool is removed later,
 * its config is simply ignored during loading.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Loading, AiGenerationPanel } from '@/components/common';
import {
  Save,
  RotateCcw,
  AlertCircle,
  Wrench,
  Search,
  ChevronRight,
  Check,
  Star,
  Package,
  AlertTriangle,
  Trash2,
  Play,
  X,
  CheckCircle2,
  XCircle,
  Clock,
  Code,
  FormInput,
  Server,
  Puzzle,
  Zap,
} from 'lucide-react';
import { useToolEnhancementConfig, useUpdateToolEnhancementConfig } from '../hooks/useSystemConfig';
import { useToolsQuery } from '@/lib/queries';
import { useI18n } from '@/i18n';
import { toolGenerationApi, type ReferenceToolConfig } from '@/lib/api/toolGeneration';
import { testTool, getToolSchema, type ToolTestResponse, type ToolSchemaResponse } from '@/lib/api/tools';
import type { ToolEnhancementConfig, ToolEnhancementToolConfig } from '@/types/system';
import type { CustomTool, BuiltinToolkit, MCPTool, DynamicTool } from '@/types/tools';
import { useAvailableMCPServers } from '@/features/tools/hooks/useTools';

// Combined tool type for display
interface DisplayTool {
  name: string;
  description: string;
  category: string;
  type: 'builtin' | 'builtin-tool' | 'custom' | 'mcp' | 'dynamic';
  module?: string;
  parentName?: string;  // For sub-tools: parent toolkit/server name
  parentDisplayName?: string;  // For display
}

export const ToolEnhancementPanel: React.FC = () => {
  const { messages, language } = useI18n();
  const isChinese = language === 'zh';
  const t = messages.systemSettings?.toolEnhancement || {
    title: 'Tool Enhancement',
    description: 'Configure display names and descriptions for tools',
    loading: 'Loading...',
    loadFailed: 'Failed to load',
    saveSuccess: 'Saved successfully',
    saveFailed: 'Failed to save',
    save: 'Save',
    saving: 'Saving...',
    reload: 'Reload',
    searchPlaceholder: 'Search tools...',
    noToolSelected: 'Select a tool from the left panel to configure',
    configured: 'Configured',
    notConfigured: 'Not configured',
    displayNameCn: 'Display Name (Chinese)',
    displayNameEn: 'Display Name (English)',
    descriptionCn: 'Description (Chinese)',
    descriptionEn: 'Description (English)',
    icon: 'Icon',
    displayOrder: 'Display Order',
    enabled: 'Enabled',
    clearConfig: 'Clear Config',
    toolInfo: 'Tool Info',
    technicalName: 'Technical Name',
    originalDescription: 'Original Description',
    allCategories: 'All Categories',
    orphanConfigs: 'Orphan Configs',
    orphanConfigsDesc: 'Configurations for tools that no longer exist',
    cleanupOrphans: 'Clean Up',
    orphanWarning: 'This tool no longer exists in the system',
    toolKey: 'Tool Key',
    categoryOverride: 'Category Override',
    categoryOverrideHint: 'Leave empty to use default category',
    originalCategory: 'Default Category',
    useDefaultCategory: 'Use Default',
    categories: {} as Record<string, string>,
    // AI Generation
    aiGenerate: 'AI Generate Descriptions',
    aiGenerateDesc: 'Analyze the original description and automatically generate user-friendly display names and descriptions.',
    aiGenerateButton: 'Generate',
    aiGenerating: 'Generating...',
    aiSelectModel: 'Select Model',
    aiSelectModelPlaceholder: 'Select a model...',
    aiExplanation: 'AI Analysis',
    aiNoDescription: 'No original description available for AI generation',
    // Reload Enhancer
    reloadEnhancer: 'Apply to LLM',
    reloadEnhancerDesc: 'Apply saved configurations to LLM tool descriptions',
    reloadEnhancerSuccess: 'Tool descriptions reloaded',
    reloadEnhancerFailed: 'Failed to reload',
    // Tool Testing
    testTool: 'Test Tool',
    testToolDesc: 'Directly invoke the tool with parameters for debugging',
    testParameters: 'Test Parameters',
    testParametersPlaceholder: 'Enter parameters in JSON format...',
    runTest: 'Run Test',
    running: 'Running...',
    testResult: 'Test Result',
    testSuccess: 'Execution Successful',
    testFailed: 'Execution Failed',
    executionTime: 'Execution Time',
    errorType: 'Error Type',
    closeModal: 'Close',
    noParameters: 'This tool requires no parameters',
    invalidJson: 'Invalid JSON format',
    requiredFields: 'Required Fields',
    optionalFields: 'Optional Fields',
    paramType: 'Type',
    paramDefault: 'Default',
    requiresUserContext: 'Requires User Context',
    requiresUserContextDesc: 'This tool requires user context. Your admin user ID will be used for testing.',
    formMode: 'Form Mode',
    jsonMode: 'JSON Mode',
    useDefaultValue: 'Use Default',
  };

  // Helper to translate category names
  const translateCategory = (category: string): string => {
    const key = category.toLowerCase();
    return t.categories?.[key] || category;
  };

  // Helper to get display name based on locale
  const getDisplayName = (toolName: string, cfg?: ToolEnhancementToolConfig): string => {
    if (!cfg) return toolName;
    if (isChinese) {
      return cfg.display_name_cn || cfg.display_name_en || toolName;
    }
    return cfg.display_name_en || cfg.display_name_cn || toolName;
  };

  // Fetch available tools from unified query hook
  const {
    data: toolsData,
    isLoading: toolsLoading,
    error: toolsError,
  } = useToolsQuery();

  // Fetch MCP servers and tools
  const {
    data: mcpData,
    isLoading: mcpLoading,
    refresh: refreshMcpServers,
  } = useAvailableMCPServers();

  // Track MCP refresh state
  const [isMcpRefreshing, setIsMcpRefreshing] = useState(false);

  // Detect if MCP servers exist but have no tools (stale connection)
  const mcpNeedsRefresh = useMemo(() => {
    if (!mcpData) return false;
    const hasServers = (mcpData.servers?.system?.length || 0) + (mcpData.servers?.user?.length || 0) > 0;
    const hasTools = (mcpData.mcp_tools?.length || 0) > 0;
    const isStale = mcpData.status === 'stale' || (mcpData.stale_servers?.length || 0) > 0;
    return hasServers && (!hasTools || isStale);
  }, [mcpData]);

  // Handle MCP refresh
  const handleRefreshMcp = async () => {
    setIsMcpRefreshing(true);
    try {
      await refreshMcpServers();
    } finally {
      setIsMcpRefreshing(false);
    }
  };

  // Fetch current enhancement config
  const { data: config, isLoading: configLoading, refetch } = useToolEnhancementConfig();
  const updateConfig = useUpdateToolEnhancementConfig();

  // Tool type for filtering
  type ToolTypeFilter = 'all' | 'builtin' | 'custom' | 'mcp' | 'dynamic';

  // Local state
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedToolType, setSelectedToolType] = useState<ToolTypeFilter>('all');
  const [localConfig, setLocalConfig] = useState<Record<string, ToolEnhancementToolConfig>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [aiExplanation, setAiExplanation] = useState<string>('');

  // Tool Testing state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testParameters, setTestParameters] = useState<string>('{}');
  const [testResult, setTestResult] = useState<ToolTestResponse | null>(null);
  const [toolSchema, setToolSchema] = useState<ToolSchemaResponse | null>(null);
  const [testJsonError, setTestJsonError] = useState<string | null>(null);
  const [useFormMode, setUseFormMode] = useState(true);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});

  // AI Generation mutation
  const generateMutation = useMutation({
    mutationFn: toolGenerationApi.generate,
  });

  // Reload enhancer mutation (syncs DB config to memory cache for LLM)
  const reloadEnhancerMutation = useMutation({
    mutationFn: async () => {
      const { systemApi } = await import('@/lib/api/system');
      return systemApi.toolEnhancement.reload();
    },
    onSuccess: (data) => {
      alert(`${t.reloadEnhancerSuccess}: ${data.tools_loaded} tools`);
    },
    onError: (err) => {
      alert(`${t.reloadEnhancerFailed}: ${(err as Error).message}`);
    },
  });

  // Tool test mutation
  const testMutation = useMutation({
    mutationFn: testTool,
    onSuccess: (data) => {
      setTestResult(data);
    },
    onError: (err) => {
      setTestResult({
        success: false,
        tool_name: selectedTool || '',
        result: null,
        error: (err as Error).message,
        error_type: 'MutationError',
        execution_time_ms: 0,
        parameters_used: {},
      });
    },
  });

  // Sync config from server
  useEffect(() => {
    if (config?.tools) {
      setLocalConfig(config.tools);
      setHasChanges(false);
    }
  }, [config]);

  // Combine builtin toolkits, their sub-tools, custom tools, and MCP tools
  const allTools = useMemo((): DisplayTool[] => {
    const tools: DisplayTool[] = [];

    // Add builtin toolkits and their sub-tools
    toolsData?.builtin_toolkits?.forEach((toolkit: BuiltinToolkit) => {
      const toolkitShortName = toolkit.name.replace('builtin:', '');

      // Add toolkit itself (parent)
      tools.push({
        name: toolkitShortName,
        description: toolkit.description,
        category: toolkit.category,
        type: 'builtin',
      });

      // Add sub-tools from toolkit
      toolkit.tools?.forEach((subTool) => {
        tools.push({
          name: subTool.full_name,  // e.g., "reasoning.think"
          description: subTool.description,
          category: toolkit.category,
          type: 'builtin-tool',
          parentName: toolkitShortName,
          parentDisplayName: toolkit.display_name,
        });
      });
    });

    // Add custom tools
    toolsData?.custom_tools?.forEach((tool: CustomTool) => {
      tools.push({
        name: tool.name,
        description: tool.description,
        category: tool.category,
        type: 'custom',
        module: tool.module,
      });
    });

    // Add MCP tools grouped by server
    mcpData?.mcp_tools?.forEach((tool: MCPTool) => {
      tools.push({
        name: tool.namespaced_name,  // e.g., "mcp:bazi-mcp:calculate_bazi_tool"
        description: tool.description,
        category: tool.category || 'mcp',
        type: 'mcp',
        parentName: tool.server_id,
        parentDisplayName: tool.server_id,
      });
    });

    // Add dynamic tools (skill tools, etc.)
    toolsData?.dynamic_tools?.forEach((tool: DynamicTool) => {
      tools.push({
        name: tool.name,
        description: tool.description,
        category: tool.category,
        type: 'dynamic',
        module: tool.module,
      });
    });

    return tools;
  }, [toolsData, mcpData]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>();
    allTools.forEach((tool) => cats.add(tool.category));
    return Array.from(cats).sort();
  }, [allTools]);

  // Count tools by type
  const toolTypeCounts = useMemo(() => {
    const counts = { all: 0, builtin: 0, custom: 0, mcp: 0, dynamic: 0 };
    allTools.forEach((tool) => {
      counts.all++;
      if (tool.type === 'builtin' || tool.type === 'builtin-tool') {
        counts.builtin++;
      } else if (tool.type === 'custom') {
        counts.custom++;
      } else if (tool.type === 'mcp') {
        counts.mcp++;
      } else if (tool.type === 'dynamic') {
        counts.dynamic++;
      }
    });
    return counts;
  }, [allTools]);

  // Filter tools by search, category, and type
  const filteredTools = useMemo(() => {
    return allTools.filter((tool) => {
      const matchesSearch =
        !searchQuery ||
        tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        localConfig[tool.name]?.display_name_cn?.includes(searchQuery) ||
        localConfig[tool.name]?.display_name_en?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = !selectedCategory || tool.category === selectedCategory;

      // Match tool type filter
      const matchesType = selectedToolType === 'all' ||
        (selectedToolType === 'builtin' && (tool.type === 'builtin' || tool.type === 'builtin-tool')) ||
        (selectedToolType === 'custom' && tool.type === 'custom') ||
        (selectedToolType === 'mcp' && tool.type === 'mcp') ||
        (selectedToolType === 'dynamic' && tool.type === 'dynamic');

      return matchesSearch && matchesCategory && matchesType;
    });
  }, [allTools, searchQuery, selectedCategory, selectedToolType, localConfig]);

  // Group tools by category for display
  const groupedTools = useMemo(() => {
    const groups: Record<string, DisplayTool[]> = {};
    filteredTools.forEach((tool) => {
      if (!groups[tool.category]) {
        groups[tool.category] = [];
      }
      groups[tool.category].push(tool);
    });
    return groups;
  }, [filteredTools]);

  // Detect orphan configs (configs for tools that no longer exist)
  const orphanConfigs = useMemo(() => {
    const existingToolNames = new Set(allTools.map((t) => t.name));
    return Object.keys(localConfig).filter((name) => !existingToolNames.has(name));
  }, [localConfig, allTools]);

  // Get selected tool data
  const selectedToolData = useMemo(() => {
    if (!selectedTool) return null;
    return allTools.find((t) => t.name === selectedTool);
  }, [selectedTool, allTools]);

  // Get selected tool config
  const selectedToolConfig = selectedTool ? localConfig[selectedTool] : null;

  // Check if a tool has config
  const hasConfig = (toolName: string) => {
    const cfg = localConfig[toolName];
    return cfg && (cfg.display_name_cn || cfg.display_name_en);
  };

  // Update tool config
  const updateToolConfig = (toolName: string, field: keyof ToolEnhancementToolConfig, value: any) => {
    setLocalConfig((prev) => ({
      ...prev,
      [toolName]: {
        ...prev[toolName],
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  // Clear tool config
  const clearToolConfig = (toolName: string) => {
    setLocalConfig((prev) => {
      const newConfig = { ...prev };
      delete newConfig[toolName];
      return newConfig;
    });
    setHasChanges(true);
  };

  // Clean up all orphan configs
  const cleanupOrphanConfigs = () => {
    if (orphanConfigs.length === 0) return;
    setLocalConfig((prev) => {
      const newConfig = { ...prev };
      orphanConfigs.forEach((name) => {
        delete newConfig[name];
      });
      return newConfig;
    });
    setHasChanges(true);
    // Clear selection if selected tool was an orphan
    if (selectedTool && orphanConfigs.includes(selectedTool)) {
      setSelectedTool(null);
    }
  };

  // Check if selected tool is an orphan
  const isSelectedToolOrphan = selectedTool ? orphanConfigs.includes(selectedTool) : false;

  // Collect reference tools from same group/category for AI generation
  const getReferenceTool = useCallback((): ReferenceToolConfig[] => {
    if (!selectedToolData) return [];

    const references: ReferenceToolConfig[] = [];

    // Find tools from same parent (for sub-tools) or same category
    const relatedTools = allTools.filter((tool) => {
      // Skip the selected tool itself
      if (tool.name === selectedToolData.name) return false;

      // Only include configured tools (those with display names)
      const cfg = localConfig[tool.name];
      if (!cfg || (!cfg.display_name_cn && !cfg.display_name_en)) return false;

      // For sub-tools, prioritize tools from the same parent
      if (selectedToolData.parentName) {
        return tool.parentName === selectedToolData.parentName;
      }

      // For parent tools or custom tools, use same category
      return tool.category === selectedToolData.category;
    });

    // Limit to 5 reference tools to avoid too much context
    relatedTools.slice(0, 5).forEach((tool) => {
      const cfg = localConfig[tool.name];
      references.push({
        tool_name: tool.name,
        original_description: tool.description,
        display_name_cn: cfg?.display_name_cn,
        display_name_en: cfg?.display_name_en,
        description_cn: cfg?.description_cn,
        description_en: cfg?.description_en,
      });
    });

    return references;
  }, [selectedToolData, allTools, localConfig]);

  // Handle AI generation
  const handleAiGenerate = async (model: string) => {
    if (!selectedTool || !selectedToolData) return;

    // Collect reference tools for consistency
    const referenceTools = getReferenceTool();

    const result = await generateMutation.mutateAsync({
      tool_name: selectedTool,
      original_description: selectedToolData.description,
      category: selectedToolData.category,
      model,
      language: 'zh',
      reference_tools: referenceTools.length > 0 ? referenceTools : undefined,
    });

    // Update form with generated values
    setLocalConfig((prev) => ({
      ...prev,
      [selectedTool]: {
        ...prev[selectedTool],
        display_name_cn: result.display_name_cn,
        display_name_en: result.display_name_en,
        description_cn: result.description_cn,
        description_en: result.description_en,
        icon: result.icon_suggestion,
      },
    }));
    setHasChanges(true);
    setAiExplanation(result.explanation);
  };

  // Clear AI explanation when selected tool changes
  useEffect(() => {
    setAiExplanation('');
  }, [selectedTool]);

  // Save config
  const handleSave = async () => {
    try {
      const newConfig: ToolEnhancementConfig = {
        tools: localConfig,
        categories: config?.categories || {},
        default_language: config?.default_language || 'cn',
      };
      await updateConfig.mutateAsync(newConfig);
      setHasChanges(false);
      alert(t.saveSuccess);
    } catch (error) {
      alert(`${t.saveFailed}: ${error}`);
    }
  };

  // Reload from server
  const handleReload = () => {
    refetch();
  };

  // Helper to get default value for a parameter type
  const getDefaultValueForType = (typeStr?: string): unknown => {
    if (!typeStr) return '';
    const lowerType = typeStr.toLowerCase();
    if (lowerType.includes('bool')) return false;
    if (lowerType.includes('int') || lowerType.includes('float') || lowerType.includes('number')) return 0;
    if (lowerType.includes('list') || lowerType.includes('array')) return [];
    if (lowerType.includes('dict') || lowerType.includes('object')) return {};
    return '';
  };

  // Open test modal and fetch schema
  const handleOpenTestModal = async () => {
    if (!selectedTool) return;

    setShowTestModal(true);
    setTestResult(null);
    setTestParameters('{}');
    setTestJsonError(null);
    setFormValues({});
    setUseFormMode(true);

    try {
      const schema = await getToolSchema(selectedTool);
      setToolSchema(schema);

      // Build default parameters from schema for both form and JSON mode
      const defaultParams: Record<string, unknown> = {};
      if (schema.parameters_info) {
        for (const [key, info] of Object.entries(schema.parameters_info)) {
          if (info.default !== undefined) {
            defaultParams[key] = info.default;
          } else if (info.required) {
            // Add placeholder for required params
            defaultParams[key] = getDefaultValueForType(info.type);
          }
        }
      }
      setFormValues(defaultParams);
      if (Object.keys(defaultParams).length > 0) {
        setTestParameters(JSON.stringify(defaultParams, null, 2));
      }
    } catch (err) {
      console.error('Failed to fetch tool schema:', err);
      setToolSchema(null);
    }
  };

  // Run tool test
  const handleRunTest = () => {
    if (!selectedTool) return;

    let params: Record<string, unknown>;

    if (useFormMode) {
      // Use form values directly
      params = { ...formValues };
      setTestJsonError(null);
    } else {
      // Parse JSON
      try {
        params = JSON.parse(testParameters);
        setTestJsonError(null);
      } catch (err) {
        setTestJsonError(t.invalidJson || 'Invalid JSON format');
        return;
      }
    }

    testMutation.mutate({
      tool_name: selectedTool,
      parameters: params,
    });
  };

  // Update form value
  const updateFormValue = (key: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  // Sync form values to JSON when switching modes
  const handleModeSwitch = (toFormMode: boolean) => {
    if (toFormMode) {
      // Switching to form mode - parse JSON into form values
      try {
        const parsed = JSON.parse(testParameters);
        setFormValues(parsed);
        setTestJsonError(null);
      } catch {
        // If JSON is invalid, keep current form values
      }
    } else {
      // Switching to JSON mode - serialize form values
      setTestParameters(JSON.stringify(formValues, null, 2));
    }
    setUseFormMode(toFormMode);
  };

  // Close test modal
  const handleCloseTestModal = () => {
    setShowTestModal(false);
    setTestResult(null);
    setToolSchema(null);
    setTestParameters('{}');
    setTestJsonError(null);
    setFormValues({});
    setUseFormMode(true);
  };

  if (toolsLoading || configLoading || mcpLoading) {
    return <Loading message={t.loading} />;
  }

  if (toolsError) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{t.loadFailed}: {(toolsError as Error).message}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            {t.title}
          </h2>
          <p className="text-sm text-gray-500">{t.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={handleReload} disabled={updateConfig.isPending}>
            <RotateCcw className="w-4 h-4 mr-1" />
            {t.reload}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => reloadEnhancerMutation.mutate()}
            disabled={reloadEnhancerMutation.isPending}
            title={t.reloadEnhancerDesc}
          >
            <RotateCcw className={`w-4 h-4 mr-1 ${reloadEnhancerMutation.isPending ? 'animate-spin' : ''}`} />
            {t.reloadEnhancer}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!hasChanges || updateConfig.isPending}>
            <Save className="w-4 h-4 mr-1" />
            {updateConfig.isPending ? t.saving : t.save}
          </Button>
        </div>
      </div>

      {/* MCP Refresh Warning */}
      {mcpNeedsRefresh && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <Server className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-amber-800 font-medium">
              MCP 工具未加载
            </p>
            <p className="text-xs text-amber-600 mt-0.5">
              检测到 {(mcpData?.servers?.system?.length || 0) + (mcpData?.servers?.user?.length || 0)} 个 MCP 服务器，但工具列表为空。点击刷新重新连接。
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRefreshMcp}
            disabled={isMcpRefreshing}
            className="bg-amber-100 hover:bg-amber-200 text-amber-700"
          >
            <RotateCcw className={`w-4 h-4 mr-1 ${isMcpRefreshing ? 'animate-spin' : ''}`} />
            刷新 MCP
          </Button>
        </div>
      )}

      {/* Main Split View */}
      <div className="flex gap-4 h-[calc(100vh-280px)] min-h-[500px]">
        {/* Left Panel - Tool List */}
        <Card className="w-1/3 flex flex-col overflow-hidden">
          {/* Tool Type Filter Tabs */}
          <div className="flex border-b bg-gray-50">
            {([
              { key: 'all', label: '全部', icon: null, count: toolTypeCounts.all, color: '' },
              { key: 'builtin', label: '内置', icon: Star, count: toolTypeCounts.builtin, color: 'text-yellow-500' },
              { key: 'custom', label: '自定义', icon: Package, count: toolTypeCounts.custom, color: 'text-blue-500' },
              { key: 'mcp', label: 'MCP', icon: Server, count: toolTypeCounts.mcp, color: 'text-purple-500' },
              { key: 'dynamic', label: '动态', icon: Zap, count: toolTypeCounts.dynamic, color: 'text-amber-500' },
            ] as const).map(({ key, label, icon: Icon, count, color }) => (
              <button
                key={key}
                onClick={() => setSelectedToolType(key)}
                className={`flex-1 px-2 py-2 text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                  selectedToolType === key
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {Icon && <Icon className={`w-3 h-3 ${selectedToolType === key ? 'text-blue-600' : color}`} />}
                <span>{label}</span>
                <span className={`text-xs ${selectedToolType === key ? 'text-blue-500' : 'text-gray-400'}`}>
                  ({count})
                </span>
              </button>
            ))}
          </div>

          {/* Search and Filter */}
          <div className="p-3 border-b space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <select
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value || null)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md"
            >
              <option value="">{t.allCategories} ({filteredTools.length})</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {translateCategory(cat)} ({groupedTools[cat]?.length || 0})
                </option>
              ))}
            </select>
          </div>

          {/* Tool List */}
          <div className="flex-1 overflow-y-auto">
            {Object.entries(groupedTools).map(([category, tools]) => (
              <div key={category}>
                <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase sticky top-0">
                  {translateCategory(category)} ({tools.length})
                </div>
                {tools.map((tool) => {
                  const isSubTool = tool.type === 'builtin-tool' || tool.type === 'mcp';
                  const iconClass = "w-3.5 h-3.5 flex-shrink-0";

                  // Get appropriate icon based on tool type
                  const getToolIcon = () => {
                    switch (tool.type) {
                      case 'builtin':
                        return <Star className={`${iconClass} text-yellow-500`} />;
                      case 'builtin-tool':
                        return <Puzzle className={`${iconClass} text-yellow-400`} />;
                      case 'mcp':
                        return <Server className={`${iconClass} text-purple-500`} />;
                      case 'dynamic':
                        return <Zap className={`${iconClass} text-amber-500`} />;
                      case 'custom':
                      default:
                        return <Package className={`${iconClass} text-blue-500`} />;
                    }
                  };

                  return (
                    <div
                      key={tool.name}
                      onClick={() => setSelectedTool(tool.name)}
                      className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        selectedTool === tool.name ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                      } ${isSubTool ? 'pl-6 py-1.5' : 'px-3 py-2'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {getToolIcon()}
                          <span className={`text-sm truncate ${isSubTool ? 'text-gray-700' : 'font-medium text-gray-900'}`}>
                            {getDisplayName(tool.name, localConfig[tool.name])}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 pr-3">
                          {hasConfig(tool.name) && (
                            <Check className="w-4 h-4 text-green-500" />
                          )}
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                      {/* Always show tool key below display name */}
                      <p className={`text-xs text-gray-500 truncate mt-0.5 font-mono ${isSubTool ? 'ml-5' : 'ml-5'}`}>
                        {tool.name}
                      </p>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Orphan Configs Section */}
            {orphanConfigs.length > 0 && (
              <div>
                <div className="px-3 py-2 bg-amber-50 text-xs font-medium text-amber-700 uppercase sticky top-0 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {t.orphanConfigs} ({orphanConfigs.length})
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      cleanupOrphanConfigs();
                    }}
                    className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    {t.cleanupOrphans}
                  </button>
                </div>
                {orphanConfigs.map((name) => (
                  <div
                    key={name}
                    onClick={() => setSelectedTool(name)}
                    className={`px-3 py-2 cursor-pointer border-b border-gray-100 hover:bg-amber-50 transition-colors bg-amber-50/30 ${
                      selectedTool === name ? 'bg-amber-100 border-l-2 border-l-amber-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-700 truncate">
                          {getDisplayName(name, localConfig[name])}
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                    <p className="text-xs text-amber-600 truncate mt-0.5 ml-5 font-mono">
                      {name}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="p-2 border-t bg-gray-50 text-xs text-gray-500 flex justify-between">
            <span>{Object.keys(localConfig).length - orphanConfigs.length} / {allTools.length} {t.configured}</span>
            {orphanConfigs.length > 0 && (
              <span className="text-amber-600">
                ⚠️ {orphanConfigs.length} orphan
              </span>
            )}
          </div>
        </Card>

        {/* Right Panel - Config Form */}
        <Card className="flex-1 overflow-y-auto">
          {selectedTool && (selectedToolData || isSelectedToolOrphan) ? (
            <div className="p-4 space-y-4">
              {/* Orphan Warning */}
              {isSelectedToolOrphan && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">{t.orphanWarning}</p>
                    <p className="text-xs text-amber-600 mt-1">
                      Technical name: <code className="bg-amber-100 px-1 rounded">{selectedTool}</code>
                    </p>
                  </div>
                </div>
              )}

              {/* Tool Info - only show for existing tools */}
              {selectedToolData && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">{t.toolInfo}</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">{t.technicalName}:</span>
                      <span className="ml-2 font-mono text-gray-900">{selectedToolData.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Category:</span>
                      <span className="ml-2 text-gray-900">{selectedToolData.category}</span>
                    </div>
                  </div>
                  {/* Show parent info for sub-tools */}
                  {selectedToolData.parentName && (
                    <div className="mt-2 text-sm">
                      <span className="text-gray-500">
                        {selectedToolData.type === 'mcp' ? 'MCP Server:' : 'Toolkit:'}
                      </span>
                      <span className="ml-2 text-gray-900">
                        {selectedToolData.parentDisplayName || selectedToolData.parentName}
                      </span>
                    </div>
                  )}
                  {/* Show tool type badge */}
                  <div className="mt-2 flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      selectedToolData.type === 'builtin'
                        ? 'bg-yellow-100 text-yellow-800'
                        : selectedToolData.type === 'builtin-tool'
                        ? 'bg-yellow-50 text-yellow-700'
                        : selectedToolData.type === 'mcp'
                        ? 'bg-purple-100 text-purple-800'
                        : selectedToolData.type === 'dynamic'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {selectedToolData.type === 'builtin' && '内置工具包'}
                      {selectedToolData.type === 'builtin-tool' && '内置子工具'}
                      {selectedToolData.type === 'mcp' && 'MCP 工具'}
                      {selectedToolData.type === 'dynamic' && '动态工具（技能）'}
                      {selectedToolData.type === 'custom' && '自定义工具'}
                    </span>
                  </div>
                  <div className="mt-2">
                    <span className="text-gray-500 text-sm">{t.originalDescription}:</span>
                    <p className="text-sm text-gray-700 mt-1">{selectedToolData.description}</p>
                  </div>
                </div>
              )}

              {/* AI Generation Panel - only for existing tools with descriptions */}
              {selectedToolData && selectedToolData.description && (
                <AiGenerationPanel
                  title={t.aiGenerate}
                  description={t.aiGenerateDesc}
                  generateButtonText={t.aiGenerateButton}
                  generatingText={t.aiGenerating}
                  selectModelLabel={t.aiSelectModel}
                  selectModelPlaceholder={t.aiSelectModelPlaceholder}
                  explanationTitle={t.aiExplanation}
                  isGenerating={generateMutation.isPending}
                  onGenerate={handleAiGenerate}
                  explanation={aiExplanation}
                  validateBeforeGenerate={() => {
                    if (!selectedToolData?.description) {
                      return t.aiNoDescription;
                    }
                    return null;
                  }}
                />
              )}

              {/* Tool Testing Section - only for existing tools (exclude dynamic tools as they have no func) */}
              {selectedToolData && selectedToolData.type !== 'dynamic' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-purple-900 flex items-center gap-2">
                        <Play className="w-4 h-4" />
                        {t.testTool || 'Test Tool'}
                      </h4>
                      <p className="text-xs text-purple-700 mt-1">
                        {t.testToolDesc || 'Directly invoke the tool with parameters for debugging'}
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleOpenTestModal}
                      className="bg-purple-100 hover:bg-purple-200 text-purple-700"
                    >
                      <Play className="w-4 h-4 mr-1" />
                      {t.testTool || 'Test'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Category Override */}
              {selectedToolData && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.categoryOverride || 'Category Override'}
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <Input
                        value={selectedToolConfig?.category || ''}
                        onChange={(e) => updateToolConfig(selectedTool, 'category', e.target.value)}
                        placeholder={selectedToolData.category}
                        className={selectedToolConfig?.category ? '' : 'text-gray-400'}
                      />
                    </div>
                    {selectedToolConfig?.category && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => updateToolConfig(selectedTool, 'category', '')}
                        className="flex-shrink-0"
                      >
                        {t.useDefaultCategory || 'Use Default'}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t.originalCategory || 'Default Category'}: <code className="bg-gray-100 px-1 rounded">{selectedToolData.category}</code>
                    {selectedToolConfig?.category && (
                      <span className="ml-2 text-amber-600">
                        → <code className="bg-amber-100 px-1 rounded">{selectedToolConfig.category}</code>
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Display Name CN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.displayNameCn}
                </label>
                <Input
                  value={selectedToolConfig?.display_name_cn || ''}
                  onChange={(e) => updateToolConfig(selectedTool, 'display_name_cn', e.target.value)}
                  placeholder="例如：本命盘计算"
                />
              </div>

              {/* Display Name EN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.displayNameEn}
                </label>
                <Input
                  value={selectedToolConfig?.display_name_en || ''}
                  onChange={(e) => updateToolConfig(selectedTool, 'display_name_en', e.target.value)}
                  placeholder="e.g., Calculate Natal Chart"
                />
              </div>

              {/* Description CN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.descriptionCn}
                </label>
                <textarea
                  value={selectedToolConfig?.description_cn || ''}
                  onChange={(e) => updateToolConfig(selectedTool, 'description_cn', e.target.value)}
                  placeholder="工具的中文描述"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                  rows={2}
                />
              </div>

              {/* Description EN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.descriptionEn}
                </label>
                <textarea
                  value={selectedToolConfig?.description_en || ''}
                  onChange={(e) => updateToolConfig(selectedTool, 'description_en', e.target.value)}
                  placeholder="Tool description in English"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                  rows={2}
                />
              </div>

              {/* Icon and Display Order */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.icon}
                  </label>
                  <Input
                    value={selectedToolConfig?.icon || ''}
                    onChange={(e) => updateToolConfig(selectedTool, 'icon', e.target.value)}
                    placeholder="star, heart, sun..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.displayOrder}
                  </label>
                  <Input
                    type="number"
                    value={selectedToolConfig?.display_order || 0}
                    onChange={(e) => updateToolConfig(selectedTool, 'display_order', parseInt(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Enabled Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">{t.enabled}</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedToolConfig?.enabled !== false}
                    onChange={(e) => updateToolConfig(selectedTool, 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Clear Config Button */}
              {hasConfig(selectedTool) && (
                <div className="pt-4 border-t">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => clearToolConfig(selectedTool)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    {t.clearConfig}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <Wrench className="w-12 h-12 mx-auto mb-2 opacity-20" />
                <p>{t.noToolSelected}</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Tool Test Modal */}
      {showTestModal && selectedTool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Play className="w-5 h-5 text-purple-600" />
                {t.testTool || 'Test Tool'}: {selectedTool}
              </h3>
              <button
                onClick={handleCloseTestModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* User Context Note */}
              {toolSchema?.requires_user_context && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">
                      {t.requiresUserContext || 'Requires User Context'}
                    </p>
                    <p className="text-xs text-blue-600 mt-0.5">
                      {t.requiresUserContextDesc || 'This tool requires user context. Your admin user ID will be used for testing.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Schema Info */}
              {toolSchema?.parameters_info && Object.keys(toolSchema.parameters_info).length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    {t.testParameters || 'Parameters'}
                  </h4>
                  <div className="space-y-2">
                    {/* Required fields */}
                    {Object.entries(toolSchema.parameters_info).filter(([_, info]) => info.required).length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-red-600">{t.requiredFields || 'Required'}:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {Object.entries(toolSchema.parameters_info)
                            .filter(([_, info]) => info.required)
                            .map(([key, info]) => (
                              <span
                                key={key}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-xs"
                              >
                                <span className="font-mono text-red-800">{key}</span>
                                {info.type && (
                                  <span className="text-red-600">({info.type})</span>
                                )}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                    {/* Optional fields */}
                    {Object.entries(toolSchema.parameters_info).filter(([_, info]) => !info.required).length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-gray-500">{t.optionalFields || 'Optional'}:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {Object.entries(toolSchema.parameters_info)
                            .filter(([_, info]) => !info.required)
                            .map(([key, info]) => (
                              <span
                                key={key}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-xs"
                              >
                                <span className="font-mono text-gray-700">{key}</span>
                                {info.type && (
                                  <span className="text-gray-500">({info.type})</span>
                                )}
                                {info.default !== undefined && (
                                  <span className="text-gray-400">= {JSON.stringify(info.default)}</span>
                                )}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Parameters Input */}
              <div>
                {/* Mode Toggle */}
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t.testParameters || 'Test Parameters'}
                  </label>
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => handleModeSwitch(true)}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                        useFormMode
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <FormInput className="w-3 h-3" />
                      {t.formMode || 'Form'}
                    </button>
                    <button
                      onClick={() => handleModeSwitch(false)}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                        !useFormMode
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Code className="w-3 h-3" />
                      {t.jsonMode || 'JSON'}
                    </button>
                  </div>
                </div>

                {useFormMode ? (
                  /* Form Mode */
                  <div className="space-y-3">
                    {toolSchema?.parameters_info && Object.keys(toolSchema.parameters_info).length > 0 ? (
                      Object.entries(toolSchema.parameters_info).map(([key, info]) => {
                        const typeStr = info.type?.toLowerCase() || '';
                        const isBool = typeStr.includes('bool');
                        const isNumber = typeStr.includes('int') || typeStr.includes('float') || typeStr.includes('number');
                        const isComplex = typeStr.includes('list') || typeStr.includes('array') || typeStr.includes('dict') || typeStr.includes('object');
                        const value = formValues[key];

                        return (
                          <div key={key} className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-medium text-gray-700 font-mono">
                                {key}
                                {info.required && <span className="text-red-500 ml-0.5">*</span>}
                              </label>
                              {info.type && (
                                <span className="text-xs text-gray-400">({info.type})</span>
                              )}
                              {info.default !== undefined && (
                                <button
                                  onClick={() => updateFormValue(key, info.default)}
                                  className="text-xs text-blue-500 hover:text-blue-700"
                                >
                                  {t.useDefaultValue || 'Use Default'}
                                </button>
                              )}
                            </div>
                            {isBool ? (
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={Boolean(value)}
                                  onChange={(e) => updateFormValue(key, e.target.checked)}
                                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                />
                                <span className="text-sm text-gray-600">{value ? 'true' : 'false'}</span>
                              </label>
                            ) : isNumber ? (
                              <input
                                type="number"
                                value={typeof value === 'number' ? value : ''}
                                onChange={(e) => updateFormValue(key, e.target.value === '' ? '' : Number(e.target.value))}
                                placeholder={info.default !== undefined ? String(info.default) : '0'}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-purple-500 focus:border-purple-500"
                              />
                            ) : isComplex ? (
                              <textarea
                                value={typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value || '')}
                                onChange={(e) => {
                                  try {
                                    const parsed = JSON.parse(e.target.value);
                                    updateFormValue(key, parsed);
                                  } catch {
                                    // Keep raw string if invalid JSON
                                    updateFormValue(key, e.target.value);
                                  }
                                }}
                                placeholder={info.default !== undefined ? JSON.stringify(info.default) : '[]'}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm font-mono resize-none focus:ring-purple-500 focus:border-purple-500"
                                rows={2}
                              />
                            ) : (
                              <input
                                type="text"
                                value={String(value ?? '')}
                                onChange={(e) => updateFormValue(key, e.target.value)}
                                placeholder={info.default !== undefined ? String(info.default) : ''}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-purple-500 focus:border-purple-500"
                              />
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-500 italic py-2">
                        {t.noParameters || 'This tool requires no parameters'}
                      </p>
                    )}
                  </div>
                ) : (
                  /* JSON Mode */
                  <>
                    <textarea
                      value={testParameters}
                      onChange={(e) => setTestParameters(e.target.value)}
                      placeholder={t.testParametersPlaceholder || 'Enter parameters in JSON format...'}
                      className={`w-full px-3 py-2 border rounded-md text-sm font-mono resize-none ${
                        testJsonError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      rows={6}
                    />
                    {testJsonError && (
                      <p className="text-xs text-red-600 mt-1">{testJsonError}</p>
                    )}
                  </>
                )}
              </div>

              {/* Test Result */}
              {testResult && (
                <div
                  className={`rounded-lg p-4 ${
                    testResult.success
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {testResult.success ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span
                      className={`font-medium ${
                        testResult.success ? 'text-green-800' : 'text-red-800'
                      }`}
                    >
                      {testResult.success ? t.testSuccess || 'Success' : t.testFailed || 'Failed'}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-500 ml-auto">
                      <Clock className="w-3.5 h-3.5" />
                      {testResult.execution_time_ms.toFixed(2)}ms
                    </span>
                  </div>

                  {testResult.error && (
                    <div className="mt-2">
                      {testResult.error_type && (
                        <p className="text-xs text-red-600 mb-1">
                          {t.errorType || 'Error Type'}: {testResult.error_type}
                        </p>
                      )}
                      <pre className="text-xs text-red-700 bg-red-100 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                        {testResult.error}
                      </pre>
                    </div>
                  )}

                  {testResult.success && testResult.result !== null && (
                    <div className="mt-2">
                      <p className="text-xs text-green-600 mb-1">{t.testResult || 'Result'}:</p>
                      <pre className="text-xs text-green-800 bg-green-100 rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-60">
                        {typeof testResult.result === 'string'
                          ? testResult.result
                          : JSON.stringify(testResult.result, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-gray-50">
              <Button variant="secondary" size="sm" onClick={handleCloseTestModal}>
                {t.closeModal || 'Close'}
              </Button>
              <Button
                size="sm"
                onClick={handleRunTest}
                disabled={testMutation.isPending}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Play className="w-4 h-4 mr-1" />
                {testMutation.isPending ? t.running || 'Running...' : t.runTest || 'Run Test'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
